import type { IAdminUserRepository } from "@/application/ports/repositories";
import type { AdminUser, AdminUserDetails, UserFilters, UserListResponse } from "@/domain/admin";
import type { UserResponse } from "../generated/types.gen";
import * as sdk from "../generated/sdk.gen";
import { unwrap } from "../unwrap";

function toAdminUser(user: UserResponse): AdminUser {
  return {
    uuid: user.uuid,
    name: user.full_name,
    email: user.email,
    profile_picture_signed_url: user.profile_picture_signed_url ?? undefined,
    status: user.is_active ? "active" : "inactive",
    is_platform_admin: user.is_platform_admin,
    created_at: user.created_at ?? "",
    updated_at: user.updated_at ?? "",
    last_login_at: user.last_login_at ?? undefined,
    organization_count: 0,
  };
}

export class AdminUserRepository implements IAdminUserRepository {
  async getUsers(filters?: UserFilters): Promise<UserListResponse> {
    const users = await unwrap(
      sdk.listUsers({
        query: {
          limit: filters?.limit,
          is_active:
            filters?.status === "active"
              ? true
              : filters?.status === "inactive"
                ? false
                : undefined,
          is_platform_admin: filters?.is_platform_admin,
        },
      }),
    );

    const mappedUsers = users.map(toAdminUser);
    return {
      users: mappedUsers,
      total: mappedUsers.length,
      page: 1,
      limit: filters?.limit ?? 20,
      total_pages: 1,
    };
  }

  async getUser(uuid: string): Promise<AdminUserDetails> {
    const user = await unwrap(sdk.getUserByUuid({ path: { user_uuid: uuid } }));
    return toAdminUser(user);
  }
  async toggleUserStatus(uuid: string): Promise<AdminUser> {
    const user = await unwrap(sdk.toggleUserStatus({ path: { user_uuid: uuid } }));
    return toAdminUser(user);
  }
  async sendResetPasswordEmail(uuid: string): Promise<{ message: string }> {
    return unwrap(sdk.sendResetPasswordEmail({ path: { user_uuid: uuid } }));
  }
}
